import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { AlertTriangle, Crown, Gem, Sparkles } from "@fleet/icons";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { useSession } from "@/lib/auth";
import {
  type components,
  evaluateQuotas,
  formatLimit,
  formatUsed,
  interpretCheckout,
  planQueryKeys,
  type QuotaEvaluation,
  type QuotaKey,
  resolvePurchaseSurface,
} from "@space/shared";
import { currentPurchasePlatform } from "@/lib/platform";
import { useI18n } from "@/providers/i18n-provider";

type ErrorModel = components["schemas"]["ErrorModel"];
type PlanConfig = components["schemas"]["BillingConfigResponse"];
type PlanUsage = components["schemas"]["BillingUsageResponse"];
type PlanCheckout = components["schemas"]["CheckoutResponse"];

/**
 * Screen for the academy's **platform plan** (MONETIZATION_QUOTAS).
 *
 * Not to be confused with `/billing`, which is the screen where a STUDENT
 * sees what they owe THEIR academy. This shows the opposite: what the
 * academy has contracted with the platform (enrollments, catalog, video, campuses).
 *
 * - With monetization OFF (default) it shows an empty state and nothing
 *   else: zero behavior change.
 * - With ON: current tier, REAL usage vs limit bars (measured server-side
 *   over the academies the user owns), a signal when something is exceeded
 *   or the server rejected writes, and upgrade via the injected `PaymentProvider`.
 * - A student (or a guest) measures 0 across the board: the screen tells
 *   them so instead of painting empty bars with no explanation.
 *
 * Institutional Tier C: there's NO ad literal here because there are no ads
 * (ADS_STRATEGY §9) — the kit's `adsEnabled` is pinned to false.
 */
export function PlanOverview() {
  const { t } = useI18n();
  const session = useSession();

  const configQuery = createApiQuery<PlanConfig, ErrorModel>(
    () => planQueryKeys.config(),
    async () => api.GET("/api/plan/config", {}),
    () => ({ staleTime: 30_000 }),
  );

  const usageQuery = createApiQuery<PlanUsage, ErrorModel>(
    () => planQueryKeys.usage(),
    async () => api.GET("/api/plan/usage", {}),
    () => ({
      staleTime: 15_000,
      enabled: !session.isVisitor() && configQuery.data?.enabled === true,
    }),
  );

  const checkoutMutation = createApiMutation(async (vars: { tier: string }) =>
    // The surface travels with the request: the server won't return a
    // payment URL to a store binary (`monetization::purchase`). Always
    // sending the field is what lets its ABSENCE fail closed there.
    api.POST("/api/plan/checkout", {
      body: { tier: vars.tier, platform: currentPurchasePlatform() },
    }),
  );

  const config = () => configQuery.data;
  const usage = () => usageQuery.data;

  /**
   * PLATFORM CLAMP — the client half.
   *
   * Without this, three rows in the admin panel (`monetization.enabled`,
   * `payments.enabled`, `payments.provider=stripe`) make an **already
   * installed APK** paint "Upgrade" and navigate to Stripe: grounds for
   * removal from Google Play, without redeploying and without a resubmit.
   * The server already closes it; this closes the other half, because the
   * published binary has no way of knowing the server changed, and because
   * a button that always fails is, in itself, a visible purchase surface
   * inside the binary.
   */
  const purchaseSurface = createMemo(() =>
    resolvePurchaseSurface({
      platform: currentPurchasePlatform(),
      enabled: config()?.enabled ?? false,
      paymentsEnabled: config()?.paymentsEnabled ?? false,
      provider: config()?.provider ?? "none",
    }),
  );

  const currentTierId = () => usage()?.tier ?? config()?.defaultTier;

  const currentTier = createMemo(() => {
    const id = currentTierId();
    return config()?.tiers.find((tier) => tier.id === id);
  });

  const evaluations = createMemo<QuotaEvaluation[]>(() => {
    const u = usage();
    if (!u) {
      return [];
    }
    return evaluateQuotas(
      {
        storage_bytes: u.limits.storageBytes ?? null,
        items: u.limits.maxItems ?? null,
        collections: u.limits.maxCollections ?? null,
        orgs: u.limits.maxOrgs ?? null,
        attachments: u.limits.maxAttachments ?? null,
      },
      {
        storage_bytes: u.usage.storageBytes,
        items: u.usage.items,
        collections: u.usage.collections,
        orgs: u.usage.orgs,
        attachments: u.usage.attachments,
      },
    );
  });

  const hasExceeded = () => (usage()?.exceeded.length ?? 0) > 0;
  const hasRejections = () => (usage()?.rejectedCount ?? 0) > 0;
  /** With no owned academies there's nothing to measure: the plan doesn't apply. */
  const ownsNoAcademy = () => usageQuery.data !== undefined && (usage()?.usage.orgs ?? 0) === 0;

  /** Academy vocabulary for the kit's five canonical keys. */
  const quotaLabel = (key: QuotaKey): string => {
    switch (key) {
      case "storage_bytes": {
        return t(msg`Video storage`);
      }
      case "items": {
        return t(msg`Enrolled students`);
      }
      case "collections": {
        return t(msg`Published courses`);
      }
      case "orgs": {
        return t(msg`Academies`);
      }
      default: {
        return t(msg`Videos`);
      }
    }
  };

  const startUpgrade = (tierId: string) => {
    checkoutMutation.mutate(
      { tier: tierId },
      {
        onSuccess: (result: PlanCheckout) => {
          // `interpretCheckout` REQUIRES the platform: it doesn't compile
          // without it. It's the last gate before `location.assign`, and
          // returns `blocked` when the surface doesn't allow that purchase.
          const action = interpretCheckout(
            {
              provider: result.provider,
              checkoutUrl: result.checkoutUrl ?? null,
              manual: result.manual,
            },
            currentPurchasePlatform(),
          );
          if (action.kind === "redirect") {
            globalThis.location.assign(action.url);
          } else if (action.kind === "blocked") {
            showToast(
              t(msg`Purchases are not available in this app. Manage your plan on the website.`),
              "info",
            );
          } else {
            showToast(
              t(msg`Plan changes are handled manually. Contact us to upgrade your academy.`),
              "info",
            );
          }
        },
        onError: () => {
          showToast(t(msg`Could not start the upgrade. Try again later.`), "error");
        },
      },
    );
  };

  return (
    <div class="space-y-6" data-testid="plan-overview">
      {/* "No response" ≠ "no plans": without this branch a network failure
          would be painted as an app with no plans (P23/CC-60 · I3). */}
      <Show when={configQuery.isError}>
        <Card class="border-destructive/50" data-testid="plan-error">
          <CardContent class="flex items-center justify-between gap-4 p-4 text-sm">
            <span>{t(msg`Could not load your plan.`)}</span>
            <Button size="sm" variant="outline" onClick={() => void configQuery.refetch()}>
              {t(msg`Retry`)}
            </Button>
          </CardContent>
        </Card>
      </Show>
      <Show when={!configQuery.isError}>
        <Show
          when={!configQuery.isLoading}
          fallback={
            <div class="space-y-3">
              <Skeleton class="h-24 w-full rounded-xl" />
              <Skeleton class="h-48 w-full rounded-xl" />
            </div>
          }
        >
          <Show
            when={config()?.enabled}
            fallback={
              <EmptyState
                icon={Gem}
                title={t(msg`Plans are not available yet`)}
                description={t(msg`This installation does not have platform plans enabled.`)}
              />
            }
          >
            {/* Current tier */}
            <Card data-testid="plan-current-tier">
              <CardHeader>
                <CardTitle class="flex items-center gap-2">
                  <Crown class="h-5 w-5 text-primary" />
                  {t(msg`Your academy's plan`)}
                </CardTitle>
              </CardHeader>
              <CardContent class="flex items-center justify-between gap-4">
                <div>
                  <div class="text-2xl font-bold" data-testid="plan-tier-label">
                    {currentTier()?.label ?? config()?.defaultTier}
                  </div>
                  <p class="text-xs text-muted-foreground">
                    {t(msg`No ads, ever: Space Academy is an institutional tool.`)}
                  </p>
                </div>
                <Badge variant="secondary">{t(msg`Ad-free`)}</Badge>
              </CardContent>
            </Card>

            {/* Signal for exceeded quota / rejected writes */}
            <Show when={hasExceeded() || hasRejections()}>
              <Card class="border-destructive/50" data-testid="quota-exceeded-banner">
                <CardContent class="flex items-start gap-3 p-4">
                  <AlertTriangle class="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                  <div class="min-w-0 text-sm">
                    <p class="font-semibold">{t(msg`Your academy reached a plan limit`)}</p>
                    <p class="text-muted-foreground">
                      <Show
                        when={hasRejections()}
                        fallback={t(
                          msg`New enrolments or videos may not be saved until you upgrade.`,
                        )}
                      >
                        {t(
                          msg`Some recent changes could not be saved to the server. Free up room or upgrade the plan.`,
                        )}
                      </Show>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Show>

            {/* Usage vs limits (users with an account) */}
            <Show
              when={!session.isVisitor()}
              fallback={
                <Card>
                  <CardContent class="p-6 text-sm text-muted-foreground">
                    {t(msg`Sign in with your academy account to see its usage.`)}
                  </CardContent>
                </Card>
              }
            >
              <Card data-testid="plan-usage">
                <CardHeader>
                  <CardTitle>{t(msg`Usage`)}</CardTitle>
                </CardHeader>
                <CardContent class="space-y-4">
                  <Show when={ownsNoAcademy()}>
                    <p class="text-sm text-muted-foreground" data-testid="plan-no-academy">
                      {t(
                        msg`You do not own an academy, so no platform limit applies to you. Studying is always unlimited.`,
                      )}
                    </p>
                  </Show>
                  <Show
                    when={!usageQuery.isLoading}
                    fallback={
                      <div class="space-y-3">
                        <Skeleton class="h-8 w-full rounded" />
                        <Skeleton class="h-8 w-full rounded" />
                        <Skeleton class="h-8 w-full rounded" />
                      </div>
                    }
                  >
                    <For each={evaluations()}>
                      {(evaluation) => (
                        <div class="space-y-1" data-testid={`quota-row-${evaluation.key}`}>
                          <div class="flex items-center justify-between text-sm">
                            <span class="font-medium">{quotaLabel(evaluation.key)}</span>
                            <span
                              classList={{
                                "text-muted-foreground": !evaluation.nearLimit,
                                "text-warning": evaluation.nearLimit && !evaluation.exceeded,
                                "text-destructive font-semibold": evaluation.exceeded,
                              }}
                              data-testid={`quota-value-${evaluation.key}`}
                            >
                              {formatUsed(evaluation.key, evaluation.used)} /{" "}
                              {formatLimit(evaluation.key, evaluation.limit)}
                            </span>
                          </div>
                          <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
                            <div
                              class="h-full rounded-full transition-all"
                              classList={{
                                "bg-primary": !evaluation.nearLimit,
                                "bg-warning": evaluation.nearLimit && !evaluation.exceeded,
                                "bg-destructive": evaluation.exceeded,
                              }}
                              style={{ width: `${Math.round(evaluation.ratio * 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </For>
                  </Show>
                </CardContent>
              </Card>
            </Show>

            {/* Available tiers + upgrade */}
            <Card data-testid="plan-tiers">
              <CardHeader>
                <CardTitle class="flex items-center gap-2">
                  <Sparkles class="h-5 w-5 text-primary" />
                  {t(msg`Available plans`)}
                </CardTitle>
              </CardHeader>
              <CardContent class="grid gap-3 sm:grid-cols-2">
                <For each={config()?.tiers ?? []}>
                  {(tier) => (
                    <div
                      class="space-y-2 rounded-xl border p-4"
                      classList={{ "border-primary": tier.id === currentTierId() }}
                      data-testid={`plan-tier-${tier.id}`}
                    >
                      <div class="flex items-center justify-between">
                        <span class="font-bold">{tier.label}</span>
                        <Show when={tier.id === currentTierId()}>
                          <Badge>{t(msg`Current`)}</Badge>
                        </Show>
                      </div>
                      <ul class="space-y-1 text-xs text-muted-foreground">
                        <li>
                          {t(msg`Enrolled students`)}:{" "}
                          {formatLimit("items", tier.limits.maxItems ?? null)}
                        </li>
                        <li>
                          {t(msg`Published courses`)}:{" "}
                          {formatLimit("collections", tier.limits.maxCollections ?? null)}
                        </li>
                        <li>
                          {t(msg`Video storage`)}:{" "}
                          {formatLimit("storage_bytes", tier.limits.storageBytes ?? null)}
                        </li>
                      </ul>
                      {/* The button only exists where the purchase is legal:
                          "Upgrade" isn't painted in the store binary. */}
                      <Show
                        when={
                          purchaseSurface().offersPurchase &&
                          !session.isVisitor() &&
                          tier.id !== currentTierId()
                        }
                      >
                        <Button
                          size="sm"
                          class="w-full"
                          disabled={checkoutMutation.isPending}
                          onClick={() => {
                            startUpgrade(tier.id);
                          }}
                          data-testid={`plan-upgrade-${tier.id}`}
                        >
                          {t(msg`Upgrade`)}
                        </Button>
                      </Show>
                    </div>
                  )}
                </For>
              </CardContent>
            </Card>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
